import React, { useState, useEffect, useRef } from 'react';
import {
  HiPlus,
  HiSearch,
  HiCalculator,
  HiCash,
  HiLightningBolt,
  HiHome,
  HiWifi,
  HiUsers,
  HiCollection,
  HiDocument,
  HiCloudUpload,
  HiX,
  HiEye,
  HiArrowUp,
  HiArrowDown,
  HiTrendingUp,
  HiCamera,
  HiRefresh,
  HiTrash,
  HiTruck,
  HiGlobe,
  HiOfficeBuilding,
  HiPhone,
  HiMoon,
  HiShieldCheck,
} from 'react-icons/hi';
import Notification from './Notification';
import { useNotification } from '../hooks/useNotification';
import { SkeletonTable } from './Skeleton';
import apiService from '../services/api';
import { normalizeApiData } from '../utils/apiHelpers';
import './Costs.css';

const Costs = () => {
  // Função para formatar valor como moeda brasileira
  const formatCurrency = value => {
    if (!value) return '';
    const numbers = value.replace(/\D/g, '');
    if (numbers === '') return '';
    const number = parseFloat(numbers) / 100;
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(number);
  };

  const [costs, setCosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [paginationInfo, setPaginationInfo] = useState({
    total: 0,
    from: 0,
    to: 0,
    per_page: 10,
    current_page: 1,
    last_page: 1
  });
  const [costType, setCostType] = useState('fixo'); // 'fixo', 'insumo', 'colaborador'
  const [formData, setFormData] = useState({
    // Campos comuns
    valor: '',
    data: new Date().toISOString().split('T')[0],
    comprovante: null,
    metodo_pagamento: 'dinheiro', // Método de pagamento
    // Campos específicos
    tipo: 'aluguel', // para custos fixos
    descricao: '', // para insumos
    nome: '', // para colaboradores
  });
  const [viewingReceipt, setViewingReceipt] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [costToCancel, setCostToCancel] = useState(null);
  const [cancelJustification, setCancelJustification] = useState('');
  const [viewingJustification, setViewingJustification] = useState(null);
  const [showCameraModal, setShowCameraModal] = useState(false);
  const [cameraFacingMode, setCameraFacingMode] = useState('environment'); // 'environment' = traseira, 'user' = frontal
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [summary, setSummary] = useState({
    total_entradas: 'R$ 0,00',
    total_despesas: 'R$ 0,00',
    saldo: 'R$ 0,00',
    total_insumos: 'R$ 0,00',
    total_colaboradores: 'R$ 0,00',
    custos_fixos: 'R$ 0,00',
  });
  const { notifications, removeNotification, showSuccess, showWarning } =
    useNotification();

  const loadSummary = async () => {
    try {
      const response = await apiService.getCostsSummary();
      // Normalizar os dados da API - pode vir em data.data ou diretamente
      const summaryData = response?.data || response || {};

      // Formatar valores se vierem como números
      const formatCurrencyValue = (value) => {
        if (!value && value !== 0) return 'R$ 0,00';
        if (typeof value === 'string') {
          // Se já está formatado, retornar como está
          if (value.includes('R$')) return value;
          // Se é número em string, formatar
          const num = parseFloat(value);
          return isNaN(num) ? 'R$ 0,00' : new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL',
          }).format(num);
        }
        // Se é número, formatar
        return new Intl.NumberFormat('pt-BR', {
          style: 'currency',
          currency: 'BRL',
        }).format(value);
      };

      setSummary({
        total_entradas: formatCurrencyValue(summaryData.total_entradas || summaryData.entradas || 0),
        total_despesas: formatCurrencyValue(summaryData.total_despesas || summaryData.despesas || 0),
        saldo: formatCurrencyValue(summaryData.saldo || 0),
        total_insumos: formatCurrencyValue(summaryData.total_insumos || summaryData.insumos || 0),
        total_colaboradores: formatCurrencyValue(summaryData.total_colaboradores || summaryData.colaboradores || 0),
        custos_fixos: formatCurrencyValue(summaryData.custos_fixos || summaryData.fixos || 0),
      });
    } catch (error) {
      console.error('Erro ao carregar resumo:', error);
      // Manter valores padrão em caso de erro
      setSummary({
        total_entradas: 'R$ 0,00',
        total_despesas: 'R$ 0,00',
        saldo: 'R$ 0,00',
        total_insumos: 'R$ 0,00',
        total_colaboradores: 'R$ 0,00',
        custos_fixos: 'R$ 0,00',
      });
    }
  };

  useEffect(() => {
    loadCosts();
    loadSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, currentPage, itemsPerPage]);

  const loadCosts = async () => {
    try {
      const data = await apiService.getCosts({
        search: searchTerm,
        page: currentPage,
        per_page: itemsPerPage,
      });
      const costsList = normalizeApiData(data);
      setCosts(costsList);

      // Armazenar informações de paginação da API
      if (data?.data) {
        setPaginationInfo({
          total: data.data.total || 0,
          from: data.data.from || 0,
          to: data.data.to || 0,
          per_page: data.data.per_page || itemsPerPage,
          current_page: data.data.current_page || currentPage,
          last_page: data.data.last_page || 1
        });
      }

      setLoading(false);
    } catch (error) {
      console.error('Erro ao carregar custos:', error);
      setCosts([]);
      setLoading(false);
    }
  };

  const handleCostTypeChange = type => {
    setCostType(type);
    // Reset form when changing type
    setFormData({
      valor: '',
      data: new Date().toISOString().split('T')[0],
      comprovante: null,
      metodo_pagamento: 'dinheiro',
      tipo: 'aluguel',
      descricao: '',
      nome: '',
    });
  };

  // Função para formatar valor para formato de moeda brasileira (ex: 2.981,23)
  const formatCurrencyValue = (value) => {
    if (!value) return '0,00';

    // Remove R$, espaços e outros caracteres não numéricos exceto vírgula e ponto
    let numbers = value.replace(/[R$\s]/g, '');

    // Se não tem vírgula nem ponto, adiciona ,00
    if (!numbers.includes(',') && !numbers.includes('.')) {
      numbers = `${numbers},00`;
    }

    // Se tem ponto mas não vírgula, assume que ponto é decimal
    if (numbers.includes('.') && !numbers.includes(',')) {
      numbers = numbers.replace('.', ',');
    }

    // Se tem ambos, remove pontos (milhares) e mantém vírgula (decimal)
    if (numbers.includes('.') && numbers.includes(',')) {
      // Remove pontos (separadores de milhares)
      numbers = numbers.replace(/\./g, '');
    }

    // Garante que tem pelo menos 2 casas decimais
    const parts = numbers.split(',');
    if (parts.length === 1) {
      numbers = `${numbers},00`;
    } else if (parts[1].length === 1) {
      numbers = `${numbers}0`;
    }

    // Adiciona pontos para milhares se necessário
    const [inteiro, decimal] = numbers.split(',');
    const inteiroFormatado = inteiro.replace(/\B(?=(\d{3})+(?!\d))/g, '.');

    return `${inteiroFormatado},${decimal || '00'}`;
  };

  // Função para formatar valor para exibição no datatable
  const formatValueForDisplay = (value) => {
    if (!value) return 'R$ 0,00';

    // Se é string, pode vir em vários formatos
    if (typeof value === 'string') {
      // Remove caracteres não numéricos exceto ponto e vírgula
      const cleanValue = value.replace(/[^\d,.-]/g, '');

      // Se tem apenas ponto (formato decimal americano: "5533.56")
      if (cleanValue.includes('.') && !cleanValue.includes(',')) {
        const numValue = parseFloat(cleanValue);
        if (!isNaN(numValue)) {
          return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL',
          }).format(numValue);
        }
      }

      // Se tem vírgula, pode estar no formato brasileiro já
      if (cleanValue.includes(',')) {
        // Converte vírgula para ponto, parseia e formata
        const numValue = parseFloat(cleanValue.replace(/\./g, '').replace(',', '.'));
        if (!isNaN(numValue)) {
          return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL',
          }).format(numValue);
        }
      }

      // Se não tem ponto nem vírgula, é um número inteiro
      const numValue = parseFloat(cleanValue);
      if (!isNaN(numValue)) {
        return new Intl.NumberFormat('pt-BR', {
          style: 'currency',
          currency: 'BRL',
        }).format(numValue);
      }
    }

    // Se é número diretamente, formata normalmente
    const numValue = typeof value === 'number' ? value : parseFloat(value) || 0;
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(numValue);
  };

  const handleAddCost = async () => {
    // Validações específicas por tipo
    if (costType === 'fixo') {
      if (!formData.valor || !formData.data) {
        showWarning('Preencha valor e data');
        return;
      }
    } else if (costType === 'insumo') {
      if (!formData.descricao || !formData.valor || !formData.data) {
        showWarning('Preencha descrição, valor e data');
        return;
      }
    } else if (costType === 'colaborador') {
      if (!formData.nome || !formData.valor) {
        showWarning('Preencha nome e valor');
        return;
      }
    }

    const tipoLabels = {
      aluguel: 'Aluguel',
      luz: 'Luz',
      agua: 'Água',
      internet: 'Internet',
      transportador: 'Transportador',
      operacao_bissau: 'Operação Bissau',
      operacao_sao_paulo: 'Operação São Paulo',
      celular: 'Celular',
      vigilancia_noturna: 'Vigilância Noturna',
      alfandega: 'Alfândega',
    };

    // Formatar valor para formato brasileiro (ex: 2.981,23)
    const valorFormatado = formatCurrencyValue(formData.valor);

    let costData;
    if (costType === 'fixo') {
      costData = {
        categoria: formData.tipo, // Categoria: aluguel, luz, agua, internet
        descricao: tipoLabels[formData.tipo],
        valor: valorFormatado, // Formato: 2.981,23
        data: formData.data,
        comprovante: formData.comprovante,
        tipo: 'Saida', // Tipo de transação
        metodo_pagamento: formData.metodo_pagamento || 'dinheiro', // Método de pagamento do formulário
      };
    } else if (costType === 'insumo') {
      costData = {
        categoria: 'insumo',
        descricao: formData.descricao,
        valor: valorFormatado, // Formato: 2.981,23
        data: formData.data,
        comprovante: formData.comprovante,
        tipo: 'Saida',
        metodo_pagamento: formData.metodo_pagamento || 'dinheiro', // Método de pagamento do formulário
      };
    } else {
      costData = {
        categoria: 'colaborador',
        descricao: formData.nome,
        valor: valorFormatado, // Formato: 2.981,23
        data: formData.data,
        comprovante: formData.comprovante,
        nome: formData.nome,
        tipo: 'Saida',
        metodo_pagamento: formData.metodo_pagamento || 'dinheiro', // Método de pagamento do formulário
      };
    }

    try {
      await apiService.createCost(costData);

    // Reset form
    setFormData({
      valor: '',
      data: new Date().toISOString().split('T')[0],
      comprovante: null,
      metodo_pagamento: 'dinheiro',
      tipo: 'aluguel',
      descricao: '',
      nome: '',
    });

    const successMessages = {
      fixo: 'Custo fixo adicionado com sucesso!',
      insumo: 'Insumo adicionado com sucesso!',
      colaborador: 'Colaborador adicionado com sucesso!',
    };
    showSuccess(successMessages[costType]);
      loadCosts();
      loadSummary();
    } catch (error) {
      console.error('Erro ao criar custo:', error);
      showWarning(error.message || 'Erro ao criar custo');
    }
  };

  const handleCancelCost = cost => {
    setCostToCancel(cost);
    setCancelJustification('');
    setShowCancelModal(true);
  };

  const confirmCancelCost = async () => {
    if (!cancelJustification.trim()) {
      showWarning('Por favor, informe a justificativa para o cancelamento');
      return;
    }

    try {
      await apiService.cancelCost(costToCancel.id, cancelJustification);
    setShowCancelModal(false);
    setCostToCancel(null);
    setCancelJustification('');
    showSuccess('Transação cancelada com sucesso!');
      loadCosts();
      loadSummary();
    } catch (error) {
      console.error('Erro ao cancelar custo:', error);
      showWarning(error.message || 'Erro ao cancelar custo');
    }
  };

  // Usar resumo da API
  const calculateTotalEntradas = () => summary.total_entradas || 'R$ 0,00';
  const calculateTotalDespesas = () => summary.total_despesas || 'R$ 0,00';
  const calculateSaldo = () => summary.saldo || 'R$ 0,00';
  const calculateTotalInsumos = () => summary.total_insumos || 'R$ 0,00';
  const calculateTotalColaboradores = () =>
    summary.total_colaboradores || 'R$ 0,00';
  const calculateTotalFixed = () => summary.custos_fixos || 'R$ 0,00';
  const calculateGrandTotal = () => summary.total_despesas || 'R$ 0,00';

  // Verificar se saldo é positivo ou negativo
  const getSaldoStatus = () => {
    const saldo =
      parseFloat(
        calculateSaldo()
          .replace('R$', '')
          .replace(/\./g, '')
          .replace(',', '.')
          .trim()
      ) || 0;
    return saldo >= 0 ? 'positive' : 'negative';
  };

  // Se houver busca local, filtrar os custos, caso contrário usar os custos da API diretamente
  const filteredCosts = Array.isArray(costs)
    ? (searchTerm
        ? costs.filter(cost =>
            cost.descricao && cost.descricao.toLowerCase().includes(searchTerm.toLowerCase())
          )
        : costs)
    : [];

  // Usar paginação da API se disponível, caso contrário usar paginação local
  const totalPages = paginationInfo.last_page || Math.ceil(filteredCosts.length / itemsPerPage);
  const paginatedCosts = searchTerm
    ? filteredCosts.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
    : filteredCosts;

  // Calcular índice inicial para numeração das linhas
  const startIndex = paginationInfo.from > 0
    ? paginationInfo.from - 1
    : (currentPage - 1) * itemsPerPage;

  const getCategoryIcon = categoria => {
    switch (categoria) {
      case 'insumo':
        return <HiCash size={18} />;
      case 'colaborador':
        return <HiUsers size={18} />;
      case 'aluguel':
        return <HiHome size={18} />;
      case 'luz':
        return <HiLightningBolt size={18} />;
      case 'agua':
        return <HiCollection size={18} />;
      case 'internet':
        return <HiWifi size={18} />;
      case 'transportador':
        return <HiTruck size={18} />;
      case 'operacao_bissau':
        return <HiGlobe size={18} />;
      case 'operacao_sao_paulo':
        return <HiOfficeBuilding size={18} />;
      case 'celular':
        return <HiPhone size={18} />;
      case 'vigilancia_noturna':
        return <HiMoon size={18} />;
      case 'alfandega':
        return <HiShieldCheck size={18} />;
      default:
        return <HiCalculator size={18} />;
    }
  };

  const getCategoryBadge = categoria => {
    const badges = {
      insumo: { label: 'Insumo', class: 'badge-info' },
      colaborador: { label: 'Colaborador', class: 'badge-success' },
      aluguel: { label: 'Aluguel', class: 'badge-warning' },
      luz: { label: 'Luz', class: 'badge-warning' },
      agua: { label: 'Água', class: 'badge-info' },
      internet: { label: 'Internet', class: 'badge-info' },
      transportador: { label: 'Transportador', class: 'badge-warning' },
      operacao_bissau: { label: 'Operação Bissau', class: 'badge-warning' },
      operacao_sao_paulo: { label: 'Operação São Paulo', class: 'badge-warning' },
      celular: { label: 'Celular', class: 'badge-info' },
      vigilancia_noturna: { label: 'Vigilância Noturna', class: 'badge-info' },
      alfandega: { label: 'Alfândega', class: 'badge-warning' },
    };
    const badge = badges[categoria] || badges.insumo;
    return <span className={`badge ${badge.class}`}>{badge.label}</span>;
  };

  const handleReceiptUpload = files => {
    const file = files[0];
    if (file) {
      const fileData = {
        id: Date.now(),
        file,
        preview: URL.createObjectURL(file),
        name: file.name,
      };
      setFormData({ ...formData, comprovante: fileData });
    }
  };

  const handleRemoveReceipt = () => {
    setFormData({ ...formData, comprovante: null });
  };

  // Funções para câmera
  const handleOpenCamera = () => {
    setShowCameraModal(true);
  };

  const handleCloseCamera = () => {
    setShowCameraModal(false);
    if (videoRef.current) {
      const stream = videoRef.current.srcObject;
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
      videoRef.current.srcObject = null;
    }
  };

  const startCamera = async () => {
    try {
      // Parar stream anterior se existir
      if (videoRef.current && videoRef.current.srcObject) {
        const stream = videoRef.current.srcObject;
        stream.getTracks().forEach(track => track.stop());
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: cameraFacingMode }
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (error) {
      console.error('Erro ao acessar câmera:', error);
      showWarning('Não foi possível acessar a câmera. Verifique as permissões.');
      handleCloseCamera();
    }
  };

  const toggleCamera = async () => {
    setCameraFacingMode(prev => prev === 'environment' ? 'user' : 'environment');
    // A câmera será reiniciada no useEffect quando cameraFacingMode mudar
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    // Ajustar dimensões do canvas para o vídeo
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Desenhar frame do vídeo no canvas
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Converter canvas para blob e depois para File
    canvas.toBlob((blob) => {
      if (blob) {
        const file = new File([blob], `comprovante_${Date.now()}.jpg`, { type: 'image/jpeg' });
        const fileData = {
          id: Date.now(),
          file,
          preview: URL.createObjectURL(file),
          name: file.name,
        };
        setFormData({ ...formData, comprovante: fileData });
        showSuccess('Foto capturada com sucesso!');
        handleCloseCamera();
      }
    }, 'image/jpeg', 0.9);
  };

  useEffect(() => {
    if (showCameraModal && videoRef.current) {
      startCamera();
    }
    return () => {
      if (videoRef.current) {
        const stream = videoRef.current.srcObject;
        if (stream) {
          stream.getTracks().forEach(track => track.stop());
        }
      }
    };
  }, [showCameraModal, cameraFacingMode]);

  return (
    <div className='page-container costs-page'>
      <div className='breadcrumb'>
        <span>Painel</span>
        <span>Gestão de Custos</span>
      </div>

      <div className='costs-page-header'>
        <div className='costs-page-header-content'>
          <div className='costs-page-header-icon'>
            <HiCalculator size={28} />
          </div>
          <div>
            <h1>Gestão de Custos</h1>
            <p>Gerencie entradas, despesas e acompanhe o saldo</p>
          </div>
        </div>
      </div>

      {/* Resumo Financeiro Principal */}
      <div className='financial-summary'>
        <div className='financial-card entrada'>
          <div className='financial-icon'>
            <HiArrowUp size={32} />
          </div>
          <div className='financial-content'>
            <span className='financial-label'>Entradas</span>
            <span className='financial-value entrada-value'>
              {calculateTotalEntradas()}
            </span>
            <span className='financial-description'>Valores das remessas</span>
          </div>
        </div>

        <div className='financial-card despesa'>
          <div className='financial-icon'>
            <HiArrowDown size={32} />
          </div>
          <div className='financial-content'>
            <span className='financial-label'>Despesas</span>
            <span className='financial-value despesa-value'>
              {calculateTotalDespesas()}
            </span>
            <span className='financial-description'>
              Custos e despesas totais
            </span>
          </div>
        </div>

        <div className={`financial-card saldo ${getSaldoStatus()}`}>
          <div className='financial-icon'>
            <HiTrendingUp size={32} />
          </div>
          <div className='financial-content'>
            <span className='financial-label'>Saldo</span>
            <span className={`financial-value saldo-value ${getSaldoStatus()}`}>
              {calculateSaldo()}
            </span>
            <span className='financial-description'>
              {getSaldoStatus() === 'positive'
                ? 'Saldo positivo'
                : 'Saldo negativo'}
            </span>
          </div>
        </div>
      </div>

      {/* Resumo Detalhado de Custos */}
      <div className='costs-summary-cards'>
        <div className='summary-card'>
          <div className='summary-icon insumo'>
            <HiCash size={24} />
          </div>
          <div className='summary-content'>
            <span className='summary-label'>Total Insumos</span>
            <span className='summary-value'>{calculateTotalInsumos()}</span>
          </div>
        </div>
        <div className='summary-card'>
          <div className='summary-icon colaborador'>
            <HiUsers size={24} />
          </div>
          <div className='summary-content'>
            <span className='summary-label'>Colaboradores</span>
            <span className='summary-value'>
              {calculateTotalColaboradores()}
            </span>
          </div>
        </div>
        <div className='summary-card'>
          <div className='summary-icon fixo'>
            <HiHome size={24} />
          </div>
          <div className='summary-content'>
            <span className='summary-label'>Custos Fixos</span>
            <span className='summary-value'>{calculateTotalFixed()}</span>
          </div>
        </div>
        <div className='summary-card total'>
          <div className='summary-icon total'>
            <HiCalculator size={24} />
          </div>
          <div className='summary-content'>
            <span className='summary-label'>Total Despesas</span>
            <span className='summary-value'>{calculateGrandTotal()}</span>
          </div>
        </div>
      </div>

      {/* Formulário Unificado de Registro */}
      <div className='card unified-cost-form-card'>
        <div className='card-header'>
          <h2>Registrar Saída</h2>
        </div>
        <div className='unified-cost-form'>
          {/* Seletor de Tipo */}
          <div className='cost-type-selector'>
            <button
              type='button'
              className={`cost-type-btn ${costType === 'fixo' ? 'active' : ''}`}
              onClick={() => handleCostTypeChange('fixo')}
            >
              <HiHome size={20} />
              <span>Custo Fixo</span>
            </button>
            <button
              type='button'
              className={`cost-type-btn ${
                costType === 'insumo' ? 'active' : ''
              }`}
              onClick={() => handleCostTypeChange('insumo')}
            >
              <HiCash size={20} />
              <span>Insumo</span>
            </button>
            <button
              type='button'
              className={`cost-type-btn ${
                costType === 'colaborador' ? 'active' : ''
              }`}
              onClick={() => handleCostTypeChange('colaborador')}
            >
              <HiUsers size={20} />
              <span>Colaborador</span>
            </button>
          </div>

          {/* Formulário Dinâmico */}
          <div className='cost-form-fields'>
            <div className='form-row'>
              {/* Campo específico por tipo */}
              {costType === 'fixo' && (
                <div className='form-group'>
                  <label>Tipo de Custo</label>
                  <select
                    value={formData.tipo}
                    onChange={e =>
                      setFormData({ ...formData, tipo: e.target.value })
                    }
                  >
                    <option value='aluguel'>Aluguel</option>
                    <option value='luz'>Luz</option>
                    <option value='agua'>Água</option>
                    <option value='internet'>Internet</option>
                    <option value='transportador'>Transportador</option>
                    <option value='operacao_bissau'>Operação Bissau</option>
                    <option value='operacao_sao_paulo'>Operação São Paulo</option>
                    <option value='celular'>Celular</option>
                    <option value='vigilancia_noturna'>Vigilância Noturna</option>
                    <option value='alfandega'>Alfândega</option>
                  </select>
                </div>
              )}

              {costType === 'insumo' && (
                <div className='form-group'>
                  <label>Descrição do Insumo</label>
                  <input
                    type='text'
                    value={formData.descricao}
                    onChange={e =>
                      setFormData({ ...formData, descricao: e.target.value })
                    }
                    placeholder='Ex: Material de embalagem'
                  />
                </div>
              )}

              {costType === 'colaborador' && (
                <div className='form-group'>
                  <label>Nome do Colaborador</label>
                  <input
                    type='text'
                    value={formData.nome}
                    onChange={e =>
                      setFormData({ ...formData, nome: e.target.value })
                    }
                    placeholder='Ex: João Silva'
                  />
                </div>
              )}

              {/* Valor - sempre visível */}
              <div className='form-group'>
                <label>Valor</label>
                <input
                  type='text'
                  value={formData.valor}
                  onChange={e => {
                    const formatted = formatCurrency(e.target.value);
                    setFormData({ ...formData, valor: formatted });
                  }}
                  placeholder='R$ 0,00'
                />
              </div>

              {/* Data - fixo, insumo e colaborador */}
              {(costType === 'fixo' ||
                costType === 'colaborador' ||
                costType === 'insumo') && (
                <div className='form-group'>
                  <label>Data</label>
                  <input
                    type='date'
                    value={formData.data}
                    onChange={e =>
                      setFormData({ ...formData, data: e.target.value })
                    }
                  />
                </div>
              )}

              {/* Método de Pagamento - sempre visível */}
              <div className='form-group'>
                <label>Método de Pagamento</label>
                <select
                  value={formData.metodo_pagamento}
                  onChange={e =>
                    setFormData({ ...formData, metodo_pagamento: e.target.value })
                  }
                  required
                >
                  <option value='dinheiro'>Dinheiro</option>
                  <option value='pix'>PIX</option>
                  <option value='cartao'>Cartão</option>
                  <option value='transferencia'>Transferência</option>
                  <option value='cheque'>Cheque</option>
                </select>
              </div>
            </div>

            {/* Comprovante - dentro de cost-form-fields */}
            <div className='comprovante-container'>
              <div className='form-group'>
                <label>Comprovante</label>
                <div className="photo-options comprovante-photo-options">
                  <button
                    type='button'
                    className='btn-camera'
                    onClick={handleOpenCamera}
                  >
                    <HiCamera size={20} />
                    Tirar Foto
                  </button>
                  <span className="photo-divider">ou</span>
                  <label htmlFor="comprovante-upload" className="file-upload-label">
                    <HiCloudUpload size={20} />
                    Selecionar no Dispositivo
                    <input
                      type='file'
                      id="comprovante-upload"
                      accept='image/*,.pdf'
                      onChange={e => {
                        if (e.target.files && e.target.files.length > 0) {
                          handleReceiptUpload(e.target.files);
                        }
                      }}
                      style={{ display: 'none' }}
                    />
                  </label>
                </div>
                {formData.comprovante && (
                  <div className="nota-fiscal-list">
                    <div className="nota-fiscal-item">
                      <div className="file-info">
                        <HiDocument size={20} color="#6b7280" />
                        <span>{formData.comprovante.name || 'Comprovante'}</span>
                      </div>
                      <button
                        type='button'
                        className='btn-icon btn-delete'
                        onClick={handleRemoveReceipt}
                        title='Remover'
                      >
                        <HiTrash size={18} />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Botão Adicionar Custo - depois do comprovante */}
            <div className='form-group form-group-submit'>
              <button
                type='button'
                className='btn btn-primary btn-submit-cost'
                onClick={handleAddCost}
              >
                <HiPlus size={18} />
                Adicionar Custo
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Lista de Custos */}
      <div className='page-actions'>
        <div className='search-box'>
          <HiSearch className='search-icon' size={20} />
          <input
            type='text'
            placeholder='Buscar custos...'
            value={searchTerm}
            onChange={e => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
          />
        </div>
      </div>

      <div className='card'>
        {loading ? (
          <SkeletonTable rows={8} columns={8} />
        ) : paginatedCosts.length === 0 ? (
          <div className='empty-state'>
            <p>
              {searchTerm
                ? 'Nenhum custo encontrado'
                : 'Nenhum custo cadastrado'}
            </p>
          </div>
        ) : (
          <>
            <div className='table-container'>
              <table className='data-table'>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Categoria</th>
                    <th>Descrição</th>
                    <th>Valor</th>
                    <th>Data</th>
                    <th>Método de Pagamento</th>
                    <th>Status</th>
                    <th>Criado Por</th>
                    <th>Comprovante</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedCosts.map((cost, index) => (
                    <tr key={cost.id}>
                      <td>{startIndex + index + 1}</td>
                      <td>
                        <div className='category-cell'>
                          {getCategoryIcon(cost.categoria)}
                          {getCategoryBadge(cost.categoria)}
                        </div>
                      </td>
                      <td>
                        <strong>{cost.descricao}</strong>
                      </td>
                      <td>
                        <strong>
                          {formatValueForDisplay(cost.valor)}
                        </strong>
                      </td>
                      <td>
                        {cost.data_pagamento
                          ? new Date(cost.data_pagamento).toLocaleString('pt-BR', {
                              timeZone: 'America/Sao_Paulo',
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                              second: '2-digit'
                            })
                          : (cost.data
                              ? new Date(cost.data).toLocaleString('pt-BR', {
                                  timeZone: 'America/Sao_Paulo',
                                  day: '2-digit',
                                  month: '2-digit',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                  second: '2-digit'
                                })
                              : '-')}
                      </td>
                      <td>
                        {cost.metodo_pagamento ? (
                          <span className='badge badge-info'>
                            {cost.metodo_pagamento.charAt(0).toUpperCase() + cost.metodo_pagamento.slice(1)}
                          </span>
                        ) : (
                          <span>-</span>
                        )}
                      </td>
                      <td>
                        {cost.status && cost.status.toLowerCase() === 'cancelado' ? (
                          <span className='badge badge-error'>Cancelado</span>
                        ) : (
                          <span className='badge badge-success'>Ativo</span>
                        )}
                      </td>
                      <td>
                        {cost.user_criador?.nome || '-'}
                      </td>
                      <td>
                        {cost.comprovante_url ? (
                          <button
                            className='btn-icon btn-view'
                            onClick={() => {
                              // Criar objeto com a URL do comprovante
                              const receiptData = {
                                url: cost.comprovante_url,
                                name: cost.comprovante_url.split('/').pop() || 'Comprovante',
                                preview: cost.comprovante_url.startsWith('http')
                                  ? cost.comprovante_url
                                  : `https://apicargo.sendingto.com${cost.comprovante_url.startsWith('/') ? '' : '/'}${cost.comprovante_url}`
                              };
                              setViewingReceipt(receiptData);
                            }}
                            title='Ver comprovante'
                          >
                            <HiEye size={18} />
                          </button>
                        ) : (
                          <span className='no-receipt'>-</span>
                        )}
                      </td>
                      <td>
                        <div className='actions'>
                          {cost.status && cost.status.toLowerCase() === 'cancelado' ? (
                            <button
                              className='btn-icon btn-view'
                              onClick={() => setViewingJustification(cost)}
                              title='Ver justificativa do cancelamento'
                            >
                              <HiEye size={18} />
                            </button>
                          ) : (
                            <button
                              className='btn-icon btn-cancel'
                              onClick={() => handleCancelCost(cost)}
                              title='Cancelar transação'
                            >
                              <HiX size={18} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className='table-footer'>
              <div className='pagination-info'>
                {paginationInfo.total > 0 ? (
                  <span>
                    Mostrando {paginationInfo.from} a {paginationInfo.to} de {paginationInfo.total} registro{paginationInfo.total !== 1 ? 's' : ''}
                  </span>
                ) : (
                  <span>
                    {paginatedCosts.length > 0
                      ? `Mostrando ${(currentPage - 1) * itemsPerPage + 1} a ${Math.min(currentPage * itemsPerPage, filteredCosts.length)} de ${filteredCosts.length} registro${filteredCosts.length !== 1 ? 's' : ''}`
                      : 'Nenhum registro encontrado'
                    }
                  </span>
                )}
              </div>
              <div className='items-per-page'>
                <span>Mostrar:</span>
                <select
                  value={itemsPerPage}
                  onChange={e => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
              <div className='pagination'>
                <button
                  className='pagination-btn'
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                >
                  ‹
                </button>
                <span className='pagination-info'>
                  Página {currentPage} de {totalPages || 1}
                </span>
                <button
                  className='pagination-btn'
                  onClick={() =>
                    setCurrentPage(prev => Math.min(totalPages, prev + 1))
                  }
                  disabled={currentPage === totalPages || totalPages === 0}
                >
                  ›
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Modal para visualizar comprovante */}
      {viewingReceipt && (
        <div className='modal-overlay' onClick={() => setViewingReceipt(null)}>
          <div
            className='modal-content receipt-modal'
            onClick={e => e.stopPropagation()}
          >
            <div className='modal-header'>
              <h3>Comprovante</h3>
              <button
                className='btn-icon'
                onClick={() => setViewingReceipt(null)}
                title='Fechar'
              >
                <HiX size={20} />
              </button>
            </div>
            <div className='modal-body receipt-viewer'>
              {(viewingReceipt.preview || viewingReceipt.url) &&
                (() => {
                  const url = viewingReceipt.preview || viewingReceipt.url;
                  const isPDF = url.match(/\.(pdf)$/i);

                  if (isPDF) {
                    return (
                      <iframe
                        src={url}
                        title={viewingReceipt.name || 'Comprovante'}
                        style={{ width: '100%', height: '600px', border: 'none' }}
                      />
                    );
                  } else {
                    return (
                      <img
                        src={url}
                        alt={viewingReceipt.name || 'Comprovante'}
                        style={{ maxWidth: '100%', height: 'auto' }}
                      />
                    );
                  }
                })()}
            </div>
          </div>
        </div>
      )}

      {/* Modal para cancelar transação */}
      {showCancelModal && costToCancel && (
        <div
          className='modal-overlay'
          onClick={() => setShowCancelModal(false)}
        >
          <div
            className='modal-content cancel-modal'
            onClick={e => e.stopPropagation()}
          >
            <div className='modal-header'>
              <h3>Cancelar Transação</h3>
              <button
                className='btn-icon'
                onClick={() => {
                  setShowCancelModal(false);
                  setCostToCancel(null);
                  setCancelJustification('');
                }}
                title='Fechar'
              >
                <HiX size={20} />
              </button>
            </div>
            <div className='modal-body'>
              <div className='cancel-info'>
                <p>
                  <strong>Descrição:</strong> {costToCancel.descricao}
                </p>
                <p>
                  <strong>Valor:</strong> {costToCancel.valor}
                </p>
                <p>
                  <strong>Data:</strong>{' '}
                  {costToCancel.data_pagamento
                    ? new Date(costToCancel.data_pagamento).toLocaleString('pt-BR', {
                        timeZone: 'America/Sao_Paulo',
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })
                    : (costToCancel.data
                        ? new Date(costToCancel.data).toLocaleString('pt-BR', {
                            timeZone: 'America/Sao_Paulo',
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })
                        : '-')}
                </p>
              </div>
              <div className='form-group'>
                <label htmlFor='cancel-justification'>
                  Justificativa do Cancelamento{' '}
                  <span className='required'>*</span>
                </label>
                <textarea
                  id='cancel-justification'
                  rows='4'
                  value={cancelJustification}
                  onChange={e => setCancelJustification(e.target.value)}
                  placeholder='Informe o motivo do cancelamento desta transação...'
                  required
                />
              </div>
              <div className='modal-actions'>
                <button
                  type='button'
                  className='btn btn-secondary'
                  onClick={() => {
                    setShowCancelModal(false);
                    setCostToCancel(null);
                    setCancelJustification('');
                  }}
                >
                  Cancelar
                </button>
                <button
                  type='button'
                  className='btn btn-danger'
                  onClick={confirmCancelCost}
                >
                  Confirmar Cancelamento
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal para visualizar justificativa de cancelamento */}
      {viewingJustification && (
        <div className='modal-overlay' onClick={() => setViewingJustification(null)}>
          <div
            className='modal-content cancel-modal'
            onClick={e => e.stopPropagation()}
          >
            <div className='modal-header'>
              <h3>Justificativa do Cancelamento</h3>
              <button
                className='btn-icon'
                onClick={() => setViewingJustification(null)}
                title='Fechar'
              >
                <HiX size={20} />
              </button>
            </div>
            <div className='modal-body'>
              <div className='cancel-info'>
                <p>
                  <strong>Descrição:</strong> {viewingJustification.descricao}
                </p>
                <p>
                  <strong>Valor:</strong> {viewingJustification.valor}
                </p>
                <p>
                  <strong>Data:</strong>{' '}
                  {viewingJustification.data_cancelamento
                    ? new Date(viewingJustification.data_cancelamento).toLocaleString('pt-BR', {
                        timeZone: 'America/Sao_Paulo',
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit'
                      })
                    : (viewingJustification.data_pagamento
                        ? new Date(viewingJustification.data_pagamento).toLocaleString('pt-BR', {
                            timeZone: 'America/Sao_Paulo',
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                            second: '2-digit'
                          })
                        : (viewingJustification.data
                            ? new Date(viewingJustification.data).toLocaleString('pt-BR', {
                                timeZone: 'America/Sao_Paulo',
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                                second: '2-digit'
                              })
                            : '-'))}
                </p>
                {viewingJustification.user_cancelamento && (
                  <p>
                    <strong>Cancelado por:</strong> {viewingJustification.user_cancelamento.nome}
                  </p>
                )}
                <div className='form-group'>
                  <label>Justificativa do Cancelamento</label>
                  <textarea
                    value={viewingJustification.justificativa_cancelamento || 'N/A'}
                    disabled
                    rows={5}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #e5e7eb',
                      borderRadius: '0.375rem',
                      background: '#f9fafb',
                      cursor: 'not-allowed'
                    }}
                  />
                </div>
              </div>
              <div className='modal-actions'>
                <button
                  className='btn btn-secondary'
                  onClick={() => setViewingJustification(null)}
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Câmera */}
      {showCameraModal && (
        <div className='modal-overlay' onClick={handleCloseCamera}>
          <div className='modal-content camera-modal' onClick={(e) => e.stopPropagation()} style={{ maxWidth: '90vw', maxHeight: '90vh', width: '90%' }}>
            <div className='modal-header'>
              <h3>Tirar Foto do Comprovante</h3>
              <button
                className='btn-icon'
                onClick={handleCloseCamera}
              >
                <HiX size={20} />
              </button>
            </div>
            <div className='modal-body' style={{ padding: '1rem' }}>
              <div className='camera-container' style={{ position: 'relative', width: '100%', paddingBottom: '56.25%', backgroundColor: '#000', borderRadius: '8px', overflow: 'hidden', marginBottom: '1rem' }}>
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  className='camera-video'
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain'
                  }}
                />
                <canvas ref={canvasRef} style={{ display: 'none' }} />
              </div>
              <div className='camera-actions' style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap', padding: '0.5rem 0' }}>
                <button
                  type='button'
                  className='btn btn-secondary'
                  onClick={handleCloseCamera}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: '#6b7280',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'all 0.2s ease',
                    minWidth: '120px',
                    justifyContent: 'center'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#4b5563';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#6b7280';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <HiX size={18} />
                  Cancelar
                </button>
                <button
                  type='button'
                  className='btn btn-secondary'
                  onClick={toggleCamera}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: '#17A2B8',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'all 0.2s ease',
                    minWidth: '120px',
                    justifyContent: 'center'
                  }}
                  title={cameraFacingMode === 'environment' ? 'Usar câmera frontal' : 'Usar câmera traseira'}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#138496';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#17A2B8';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <HiRefresh size={18} />
                  {cameraFacingMode === 'environment' ? 'Frontal' : 'Traseira'}
                </button>
                <button
                  type='button'
                  className='btn btn-primary'
                  onClick={capturePhoto}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: '#10b981',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'all 0.2s ease',
                    minWidth: '140px',
                    justifyContent: 'center',
                    boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#059669';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 6px 16px rgba(16, 185, 129, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#10b981';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.3)';
                  }}
                >
                  <HiCamera size={18} />
                  Capturar Foto
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Notificações */}
      <div className='notifications-container'>
        {notifications.map(notification => (
          <Notification
            key={notification.id}
            message={notification.message}
            type={notification.type}
            duration={notification.duration}
            onClose={() => removeNotification(notification.id)}
          />
        ))}
      </div>
    </div>
  );
};

export default Costs;
